import React from "react";
import replaceNewLines from "../replaceNewLines";
import useDebounce from "./useDebounce";

function normalize(input: string): string {
  return input
    .toLocaleLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function markMatches(str: string, matches: [number, number][]) {
  if (!str.length) return null;
  let index = 0;
  const chunks: React.ReactNode[] = [];
  for (const [start, end] of matches) {
    chunks.push(replaceNewLines(str.slice(index, start)));
    chunks.push(<mark key={Math.random()}>{replaceNewLines(str.slice(start, end))}</mark>);
    index = end;
  }
  chunks.push(replaceNewLines(str.slice(index, str.length)));
  return chunks.filter(Boolean);
}

function findRanges(token: string, candidate: string) {
  const result: [number, number][] = [];
  const normalizedCandidate = normalize(candidate);
  let indexOccurence = normalizedCandidate.indexOf(token, 0);
  while (indexOccurence >= 0) {
    result.push([indexOccurence, indexOccurence + token.length]);
    indexOccurence = normalizedCandidate.indexOf(token, indexOccurence + 1);
  }
  return result;
}

function mergeRanges(ranges: [number, number][]) {
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  const result: [number, number][] = [];
  let last: null | [number, number] = null;

  for (const r of ranges) {
    if (!last || r[0] > last[1]) result.push((last = r));
    else if (r[1] > last[1]) last[1] = r[1];
  }

  return result;
}

function findAllRanges(tokens: string[], candidate: string) {
  return mergeRanges(tokens.flatMap(token => findRanges(token, candidate)));
}

export function matches<Data extends Record<string, string>>(tokens: string[], data: Data) {
  const keys = Object.keys(data) as (keyof Data)[];
  const result = keys.reduce<Partial<{ [k in keyof Data]: ReturnType<typeof markMatches> }>>(
    (acc, key) => {
      const str = data[key]!;
      acc[key] = markMatches(str, findAllRanges(tokens, str));
      return acc;
    },
    {},
  );

  return result as Required<typeof result>;
}

export function hasMatches(tokens: string[], normalizedString: string) {
  return tokens.every(token => normalizedString.includes(token));
}

export function tokenize(input: string): string[] {
  const result = input
    .match(/\w+|"[^"]+"/g)
    ?.map(chunk =>
      chunk.endsWith('"') && chunk.startsWith('"') && chunk.length > 2
        ? chunk.slice(1, chunk.length - 1) // remove ""
        : chunk,
    )
    .filter(Boolean);
  return result || [];
}

function defaultIndexFn<Data extends Record<string, unknown>>(data: Data) {
  const keys = Object.keys(data) as (keyof typeof data)[];
  return keys
    .map(k => data[k] ?? false)
    .filter(Boolean)
    .join(" ");
}

function useFilteredDatas<Data extends Record<string, unknown>>(
  datas: Data[],
  search: string,
  indexFn: (data: Data) => string = defaultIndexFn,
) {
  const normalizedDatas = React.useMemo(
    () => datas.map(d => normalize(indexFn(d))),
    [datas, indexFn],
  );

  search = React.useMemo(() => normalize(search), [search]);
  const debouncedSearch = useDebounce(search, 200);

  const tokens = React.useMemo(() => tokenize(debouncedSearch), [debouncedSearch]);

  datas = React.useMemo(
    () =>
      tokens.length
        ? datas.filter((_, index) => hasMatches(tokens, normalizedDatas[index]!))
        : datas,
    [datas, normalizedDatas, tokens],
  );

  return [datas, search !== debouncedSearch] as const;
}

export default useFilteredDatas;
